/**
 * Team Settings → General.
 *
 * Team display name (the name every persona sees in the header and on shared
 * pages) plus read-only tenant facts a Team Admin needs when contacting
 * support: portal slug, plan, seats and billing status.
 *
 * A logo upload is deliberately absent: no object-storage endpoint exists for
 * tenant branding yet, and a control that cannot persist is worse than no
 * control. See the task handoff.
 */
import { useState } from 'react';

import { useToast } from '../../app/toast-context';
import { DeskSection } from '../../components/desk-section';
import { UnsavedChangesGuard } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import type { TenantSummary } from '../../lib/api-types';

const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 120;

export interface GeneralSettingsProps {
  tenant: TenantSummary | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onSave: (patch: { name: string }) => Promise<void>;
}

export function GeneralSettings({
  tenant,
  isLoading,
  error,
  onRetry,
  onSave,
}: GeneralSettingsProps): JSX.Element {
  const { showToast } = useToast();
  const [draft, setDraft] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  if (tenant === null) {
    if (isLoading) {
      return (
        <p role="status" className="muted">
          Loading team details…
        </p>
      );
    }
    return (
      <div className="banner banner--error" role="alert">
        <p>{error ?? 'Team details could not be loaded.'}</p>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }

  const value = draft ?? tenant.name;

  async function submit(): Promise<void> {
    const trimmed = value.trim();
    if (trimmed.length < MIN_NAME_LENGTH) {
      setFieldError(`The team name must be at least ${String(MIN_NAME_LENGTH)} characters.`);
      return;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      setFieldError(`The team name must be ${String(MAX_NAME_LENGTH)} characters or fewer.`);
      return;
    }
    setFieldError(null);
    setIsSaving(true);
    try {
      await onSave({ name: trimmed });
      setDraft(null);
      showToast('Team name updated.', 'success');
    } catch (caught) {
      setFieldError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The team name could not be saved. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="team-settings__panel desk-grid">
      <UnsavedChangesGuard isDirty={draft !== null && draft !== tenant.name} />
      <DeskSection title="Team identity">
        <form
          className="team-settings__form desk-form desk-form--stacked"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          noValidate
        >
          <div className={`field${fieldError === null ? '' : ' field--invalid'}`}>
            <label className="field__label" htmlFor="team-name">
              Team display name
            </label>
            <input
              id="team-name"
              className="field__control"
              type="text"
              maxLength={MAX_NAME_LENGTH}
              value={value}
              onChange={(event) => setDraft(event.target.value)}
              aria-invalid={fieldError === null ? undefined : true}
              aria-describedby={fieldError === null ? 'team-name-hint' : 'team-name-error'}
            />
            {fieldError === null ? (
              <span className="field__hint" id="team-name-hint">
                Shown in the app header and on pages shared with fans.
              </span>
            ) : (
              <span className="field__error" id="team-name-error" role="alert">
                {fieldError}
              </span>
            )}
          </div>
          <div className="desk-form__actions">
            <button type="submit" className="btn btn--primary" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save team name'}
            </button>
            {draft === null ? null : (
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => {
                  setDraft(null);
                  setFieldError(null);
                }}
              >
                Reset
              </button>
            )}
          </div>
        </form>
      </DeskSection>

      <DeskSection title="Team portal" description="For reference when contacting support.">
        <dl className="team-settings__facts">
          <div>
            <dt>Portal address</dt>
            <dd>/app/{tenant.slug}</dd>
          </div>
          <div>
            <dt>Plan</dt>
            <dd>{tenant.plan ?? '—'}</dd>
          </div>
          <div>
            <dt>Billing status</dt>
            <dd>{tenant.billing_status}</dd>
          </div>
          <div>
            <dt>Members</dt>
            <dd>
              {tenant.member_count ?? '—'}
              {tenant.seats_limit == null ? '' : ` of ${String(tenant.seats_limit)} seats`}
            </dd>
          </div>
        </dl>
      </DeskSection>
    </div>
  );
}
